import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../../database/firebase';
import { fetchLeadsFromExternalAPI, saveAPILeadToFirebase } from '../../services/crmService';

// CRM Stages Configuration
export const CRM_STAGES = {
  LEAD: 'lead',
  SERVICE_PLANNING: 'serviceplanning',
  PROGRAM_DELIVERY: 'programdelivery',
  MONITORING: 'monitoring',
  FOLLOW_UP: 'followup',
  NO_NEED: 'noneed'
};

export const STAGE_CONFIG = {
  [CRM_STAGES.LEAD]: {
    title: 'Lead',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/20',
    description: 'New potential customers'
  },
  [CRM_STAGES.SERVICE_PLANNING]: {
    title: 'Service Planning',
    color: 'text-purple-600',
    bgColor: 'bg-purple-500/20',
    description: 'Planning services for customer'
  },
  [CRM_STAGES.PROGRAM_DELIVERY]: {
    title: 'Program Delivery',
    color: 'text-orange-600',
    bgColor: 'bg-orange-500/20',
    description: 'Actively delivering program'
  },
  [CRM_STAGES.MONITORING]: {
    title: 'Monitoring',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500/20',
    description: 'Monitoring progress'
  },
  [CRM_STAGES.FOLLOW_UP]: {
    title: 'Follow-Up',
    color: 'text-green-600',
    bgColor: 'bg-green-500/20',
    description: 'Follow-up with customer'
  },
  [CRM_STAGES.NO_NEED]: {
    title: 'No Need',
    color: 'text-red-600',
    bgColor: 'bg-red-500/20',
    description: 'Customer does not need service'
  }
};

// Async Thunks

// Fetch all users from Firestore
export const fetchCRMUsers = createAsyncThunk(
  'crm/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const usersRef = collection(db, 'socialmediauser');
      const querySnapshot = await getDocs(usersRef);
      const users = [];

      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return users;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch leads from external API (without saving to Firebase)
export const fetchAndSyncFromExternalAPI = createAsyncThunk(
  'crm/fetchAndSyncFromExternalAPI',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch data from external API
      // These leads will only be stored in Redux state, not Firebase
      // They will be saved to Firebase only when their stage changes
      const leadsData = await fetchLeadsFromExternalAPI();

      console.log('Fetched API leads (not saved to Firebase yet):', leadsData);

      return leadsData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Sync external API data to Firebase (legacy - kept for backward compatibility)
export const syncExternalAPIData = createAsyncThunk(
  'crm/syncExternalAPIData',
  async (apiData, { rejectWithValue }) => {
    try {
      const syncedUsers = [];

      for (const user of apiData) {
        // Use phone number as unique identifier
        const userRef = doc(db, 'socialmediauser', user.number);
        const userDoc = await getDoc(userRef);

        const userData = {
          name: user.name,
          number: user.number,
          address: user.address,
          services: user.services,
          stage: userDoc.exists() ? userDoc.data().stage : CRM_STAGES.LEAD,
          notes: userDoc.exists() ? userDoc.data().notes : [],
          createdAt: userDoc.exists() ? userDoc.data().createdAt : serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastContactedAt: userDoc.exists() ? userDoc.data().lastContactedAt : null,
          priority: userDoc.exists() ? userDoc.data().priority : 'medium',
          source: 'external_api'
        };

        await setDoc(userRef, userData, { merge: true });
        syncedUsers.push({
          id: user.number,
          ...userData
        });
      }

      return syncedUsers;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Create new user manually
export const createNewUser = createAsyncThunk(
  'crm/createNewUser',
  async (userData, { rejectWithValue }) => {
    try {
      // Generate unique ID using timestamp + random string
      const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const userRef = doc(db, 'socialmediauser', userId);

      const newUser = {
        name: userData.name,
        number: userData.number,
        address: userData.address || 'N/A',
        age: userData.age || null,
        weight: userData.weight || null,
        gender: userData.gender,
        services: userData.services || [],
        meeting_time: userData.meeting_time || null,
        stage: 'lead', // Always start in lead stage
        priority: userData.priority || 'medium',
        notes: [],
        appointments: [],
        source: 'manual',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastContactedAt: null
      };

      await setDoc(userRef, newUser);

      console.log('New user created successfully:', userId);

      return {
        id: userId,
        ...newUser
      };
    } catch (error) {
      console.error('Error creating new user:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Update user stage
export const updateUserStage = createAsyncThunk(
  'crm/updateStage',
  async ({ userId, newStage, userData }, { rejectWithValue, getState }) => {
    try {
      // Check if this is an API lead (has isFromAPI flag)
      const state = getState();
      const user = state.crm.users.find(u => u.id === userId);

      if (user && user.isFromAPI) {
        // This is an API lead - save it to Firebase for the first time
        console.log('API lead detected, saving to Firebase:', user);
        const savedUser = await saveAPILeadToFirebase(user, newStage);
        return { userId, newStage, savedUser, wasAPILead: true };
      } else {
        // This is an existing Firebase user - just update the stage
        const userRef = doc(db, 'socialmediauser', userId);
        await updateDoc(userRef, {
          stage: newStage,
          updatedAt: serverTimestamp()
        });

        return { userId, newStage, wasAPILead: false };
      }
    } catch (error) {
      console.error('Error updating user stage:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Add note to user
export const addNoteToUser = createAsyncThunk(
  'crm/addNote',
  async ({ userId, noteText }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const note = {
        id: Date.now().toString(),
        text: noteText,
        createdAt: new Date().toISOString(),
        createdBy: 'Admin' // You can replace this with actual user info
      };

      await updateDoc(userRef, {
        notes: arrayUnion(note),
        updatedAt: serverTimestamp()
      });

      return { userId, note };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete note from user
export const deleteNoteFromUser = createAsyncThunk(
  'crm/deleteNote',
  async ({ userId, noteId }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const notes = userDoc.data().notes || [];
        const updatedNotes = notes.filter(note => note.id !== noteId);

        await updateDoc(userRef, {
          notes: updatedNotes,
          updatedAt: serverTimestamp()
        });
      }

      return { userId, noteId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update user priority
export const updateUserPriority = createAsyncThunk(
  'crm/updatePriority',
  async ({ userId, priority }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      await updateDoc(userRef, {
        priority,
        updatedAt: serverTimestamp()
      });

      return { userId, priority };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update last contacted timestamp
export const updateLastContacted = createAsyncThunk(
  'crm/updateLastContacted',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const now = new Date().toISOString();

      await updateDoc(userRef, {
        lastContactedAt: now,
        updatedAt: serverTimestamp()
      });

      return { userId, lastContactedAt: now };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update user services
export const updateUserServices = createAsyncThunk(
  'crm/updateUserServices',
  async ({ userId, services }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);

      await updateDoc(userRef, {
        services: services,
        updatedAt: serverTimestamp()
      });

      return { userId, services };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add appointment to user
export const addAppointmentToUser = createAsyncThunk(
  'crm/addAppointment',
  async ({ userId, appointmentData }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const appointment = {
        id: Date.now().toString(),
        title: appointmentData.title,
        description: appointmentData.description,
        date: appointmentData.date,
        time: appointmentData.time,
        dateTime: new Date(`${appointmentData.date}T${appointmentData.time}`).toISOString(),
        status: 'scheduled', // scheduled, completed, cancelled
        createdAt: new Date().toISOString(),
        createdBy: 'Admin'
      };

      await updateDoc(userRef, {
        appointments: arrayUnion(appointment),
        updatedAt: serverTimestamp()
      });

      return { userId, appointment };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update appointment status
export const updateAppointmentStatus = createAsyncThunk(
  'crm/updateAppointmentStatus',
  async ({ userId, appointmentId, status }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const appointments = userDoc.data().appointments || [];
        const updatedAppointments = appointments.map(apt =>
          apt.id === appointmentId ? { ...apt, status } : apt
        );

        await updateDoc(userRef, {
          appointments: updatedAppointments,
          updatedAt: serverTimestamp()
        });

        return { userId, appointmentId, status };
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete appointment
export const deleteAppointment = createAsyncThunk(
  'crm/deleteAppointment',
  async ({ userId, appointmentId }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const appointments = userDoc.data().appointments || [];
        const updatedAppointments = appointments.filter(apt => apt.id !== appointmentId);

        await updateDoc(userRef, {
          appointments: updatedAppointments,
          updatedAt: serverTimestamp()
        });
      }

      return { userId, appointmentId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update appointment
export const updateAppointment = createAsyncThunk(
  'crm/updateAppointment',
  async ({ userId, appointmentId, appointmentData }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const appointments = userDoc.data().appointments || [];
        const updatedAppointments = appointments.map(apt =>
          apt.id === appointmentId
            ? {
              ...apt,
              title: appointmentData.title,
              description: appointmentData.description,
              date: appointmentData.date,
              time: appointmentData.time,
              dateTime: new Date(`${appointmentData.date}T${appointmentData.time}`).toISOString(),
              updatedAt: new Date().toISOString()
            }
            : apt
        );

        await updateDoc(userRef, {
          appointments: updatedAppointments,
          updatedAt: serverTimestamp()
        });

        return { userId, appointmentId, appointmentData };
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update generic user details
export const updateUserDetails = createAsyncThunk(
  'crm/updateUserDetails',
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'socialmediauser', userId);

      // Clean up undefined values from userData
      const cleanedData = Object.entries(userData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      await updateDoc(userRef, {
        ...cleanedData,
        updatedAt: serverTimestamp()
      });

      return { userId, userData: cleanedData };
    } catch (error) {
      console.error('Error updating user details:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  users: [],
  loading: false,
  error: null,
  syncing: false,
  syncError: null,
  filters: {
    stage: null,
    priority: null,
    search: ''
  },
  selectedUser: null
};

const crmSlice = createSlice({
  name: 'crm',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        stage: null,
        priority: null,
        search: ''
      };
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    // Local update for optimistic UI update
    moveUserLocal: (state, action) => {
      const { userId, newStage } = action.payload;
      const user = state.users.find(u => u.id === userId);
      if (user) {
        user.stage = newStage;
      }
    },
    // Add user to local state (for real-time updates)
    addUserLocal: (state, action) => {
      const exists = state.users.find(u => u.id === action.payload.id);
      if (!exists) {
        state.users.push(action.payload);
      }
    },
    // Update user in local state
    updateUserLocal: (state, action) => {
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = { ...state.users[index], ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchCRMUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCRMUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchCRMUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create new user
      .addCase(createNewUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
        console.log('New user added to state:', action.payload.id);
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch leads from external API
      .addCase(fetchAndSyncFromExternalAPI.pending, (state) => {
        state.syncing = true;
        state.syncError = null;
      })
      .addCase(fetchAndSyncFromExternalAPI.fulfilled, (state, action) => {
        state.syncing = false;
        // Add API leads to state (not saved to Firebase yet)
        // Remove old API leads first
        state.users = state.users.filter(u => !u.isFromAPI);

        // Filter out duplicates: check if a Firebase user already exists with the same apiId
        const newAPILeads = action.payload.filter(apiLead => {
          // Check if this API lead already exists in Firebase (has been saved)
          const existsInFirebase = state.users.some(fbUser =>
            fbUser.apiId === apiLead.apiId || fbUser.apiId === apiLead.id
          );
          return !existsInFirebase;
        });

        state.users.push(...newAPILeads);
        console.log('API leads loaded into Redux state:', newAPILeads.length, '(filtered from', action.payload.length, 'total)');
      })
      .addCase(fetchAndSyncFromExternalAPI.rejected, (state, action) => {
        state.syncing = false;
        state.syncError = action.payload;
      })

      // Sync external API data (legacy)
      .addCase(syncExternalAPIData.pending, (state) => {
        state.syncing = true;
        state.syncError = null;
      })
      .addCase(syncExternalAPIData.fulfilled, (state, action) => {
        state.syncing = false;
        // Merge synced users with existing users
        action.payload.forEach(syncedUser => {
          const index = state.users.findIndex(u => u.id === syncedUser.id);
          if (index !== -1) {
            state.users[index] = syncedUser;
          } else {
            state.users.push(syncedUser);
          }
        });
      })
      .addCase(syncExternalAPIData.rejected, (state, action) => {
        state.syncing = false;
        state.syncError = action.payload;
      })

      // Update stage
      .addCase(updateUserStage.fulfilled, (state, action) => {
        const { userId, newStage, savedUser, wasAPILead } = action.payload;
        const userIndex = state.users.findIndex(u => u.id === userId);

        if (userIndex !== -1) {
          if (wasAPILead && savedUser) {
            // Replace the API lead with the saved Firebase user
            state.users[userIndex] = {
              ...savedUser,
              isFromAPI: false // No longer an API-only lead
            };
            console.log('API lead saved to Firebase and updated in state:', savedUser.id);
          } else {
            // Just update the stage for existing Firebase users
            state.users[userIndex].stage = newStage;
          }
        }
      })

      // Add note
      .addCase(addNoteToUser.fulfilled, (state, action) => {
        const { userId, note } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user) {
          if (!user.notes) user.notes = [];
          user.notes.push(note);
        }
      })

      // Delete note
      .addCase(deleteNoteFromUser.fulfilled, (state, action) => {
        const { userId, noteId } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user && user.notes) {
          user.notes = user.notes.filter(note => note.id !== noteId);
        }
      })

      // Update priority
      .addCase(updateUserPriority.fulfilled, (state, action) => {
        const { userId, priority } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user) {
          user.priority = priority;
        }
      })

      // Update last contacted
      .addCase(updateLastContacted.fulfilled, (state, action) => {
        const { userId, lastContactedAt } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user) {
          user.lastContactedAt = lastContactedAt;
        }
      })

      // Update services
      .addCase(updateUserServices.fulfilled, (state, action) => {
        const { userId, services } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user) {
          user.services = services;
        }
      })

      // Add appointment
      .addCase(addAppointmentToUser.fulfilled, (state, action) => {
        const { userId, appointment } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user) {
          if (!user.appointments) user.appointments = [];
          user.appointments.push(appointment);
        }
      })

      // Update appointment status
      .addCase(updateAppointmentStatus.fulfilled, (state, action) => {
        const { userId, appointmentId, status } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user && user.appointments) {
          const appointment = user.appointments.find(apt => apt.id === appointmentId);
          if (appointment) {
            appointment.status = status;
          }
        }
      })

      // Delete appointment
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        const { userId, appointmentId } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user && user.appointments) {
          user.appointments = user.appointments.filter(apt => apt.id !== appointmentId);
        }
      })

      // Update appointment
      .addCase(updateAppointment.fulfilled, (state, action) => {
        const { userId, appointmentId, appointmentData } = action.payload;
        const user = state.users.find(u => u.id === userId);
        if (user && user.appointments) {
          const appointment = user.appointments.find(apt => apt.id === appointmentId);
          if (appointment) {
            appointment.title = appointmentData.title;
            appointment.description = appointmentData.description;
            appointment.date = appointmentData.date;
            appointment.time = appointmentData.time;
            appointment.dateTime = new Date(`${appointmentData.date}T${appointmentData.time}`).toISOString();
          }
        }
      })

      // Update user details
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        const { userId, userData } = action.payload;
        const userIndex = state.users.findIndex(u => u.id === userId);
        if (userIndex !== -1) {
          state.users[userIndex] = {
            ...state.users[userIndex],
            ...userData
          };
        }
      });
  }
});

export const {
  setFilter,
  clearFilters,
  setSelectedUser,
  moveUserLocal,
  addUserLocal,
  updateUserLocal
} = crmSlice.actions;

// Selectors
export const selectAllUsers = (state) => state.crm.users;
export const selectUsersByStage = (state, stage) =>
  state.crm.users.filter(user => user.stage === stage);
export const selectFilteredUsers = (state) => {
  let filtered = state.crm.users;

  if (state.crm.filters.stage) {
    filtered = filtered.filter(user => user.stage === state.crm.filters.stage);
  }

  if (state.crm.filters.priority) {
    filtered = filtered.filter(user => user.priority === state.crm.filters.priority);
  }

  if (state.crm.filters.search) {
    const search = state.crm.filters.search.toLowerCase();
    filtered = filtered.filter(user =>
      user.name.toLowerCase().includes(search) ||
      user.number.includes(search) ||
      user.address.toLowerCase().includes(search)
    );
  }

  return filtered;
};

export const selectCRMLoading = (state) => state.crm.loading;
export const selectCRMError = (state) => state.crm.error;
export const selectSyncing = (state) => state.crm.syncing;
export const selectSelectedUser = (state) => state.crm.selectedUser;

// Selector for today's appointments count
export const selectTodayAppointmentsCount = (state) => {
  const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

  let count = 0;
  state.crm.users.forEach(user => {
    if (user.appointments) {
      user.appointments.forEach(apt => {
        if (apt.date === today && apt.status === 'scheduled') {
          count++;
        }
      });
    }
  });

  return count;
};

// Selector for today's appointments with user details
export const selectTodayAppointments = (state) => {
  const today = new Date().toISOString().split('T')[0];

  const appointments = [];
  state.crm.users.forEach(user => {
    if (user.appointments) {
      user.appointments.forEach(apt => {
        if (apt.date === today && apt.status === 'scheduled') {
          appointments.push({
            ...apt,
            userId: user.id,
            userName: user.name,
            userNumber: user.number
          });
        }
      });
    }
  });

  // Sort by time
  return appointments.sort((a, b) => a.time.localeCompare(b.time));
};

export default crmSlice.reducer;
